import { Link } from 'react-router-dom';
import type { PrivacyNoticeState } from '../hooks/usePrivacyNotice';
import DraftTag from './DraftTag';

interface Props {
  readonly state: PrivacyNoticeState;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly disabled?: boolean;
  /** The express consent to the processing of financial and patrimonial data (LFPDPPP 2025
   *  art. 7), asked on the screen after sign-in. Without it, the plain acceptance of a form. */
  readonly express?: boolean;
}

/**
 * The acceptance of the privacy notice (#289), shown on the access-request form and, as the express
 * consent (`express`), on the screen after sign-in: a
 * link to the notice in force (opens in a new tab) and a box the person ticks themselves. While the
 * notice is a placeholder the line says so. When the notice cannot be read the box is not offered:
 * a form that cannot show the text cannot take the consent.
 */
export default function PrivacyNoticeConsent({
  state,
  checked,
  onChange,
  disabled = false,
  express = false,
}: Props): JSX.Element {
  if (state.unavailable) {
    return (
      <p role="alert" className="text-xs" style={{ color: '#DC2626' }}>
        The privacy notice is not available right now. Please try again later.
      </p>
    );
  }
  return (
    <label className="flex items-start gap-2 text-sm" style={{ color: '#374151' }}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled || state.notice === null}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1"
      />
      <span>
        {express
          ? 'I give my express consent to the processing of my financial and patrimonial data under the'
          : 'I have read and accept the'}{' '}
        <Link to="/privacy-notice" target="_blank" rel="noreferrer" className="underline">
          {express ? 'Privacy Notice' : 'privacy notice'}
        </Link>
        .{state.notice?.is_draft && <DraftTag />}
      </span>
    </label>
  );
}
